/**
 * Fishing rules, shared so the client's tooltips say exactly what the server will do.
 *
 * How a spot gets fished: of the methods its water supports, the server picks the
 * highest-level one you are fully ready for (tool in your pack that you can use, bait or
 * feathers if it needs them, the level for at least one of its fish, and a fish that is
 * biting at this time and weather). So what you carry decides what you go for.
 */
import { isNight } from './constants.js';
import type { CharacterState } from './character.js';
import {
  METHODS, SPECIES, SPECIES_IDS, WATERS, isTwilight, speciesDef,
  type Method, type SpeciesId, type WaterKind, type Weather,
} from './content/fishing.js';
import { SKILL_NAMES } from './content/skills.js';
import { TOOLS, type ToolId } from './content/tools.js';
import { bestTool, level, supplyBlocker, toolBlocker, toolPower, type Blocker } from './rules.js';

export interface FishCtx { dayT: number; weather: Weather; hidden?: boolean }
export interface FishChoice { method: Method; tool: ToolId; species: { id: SpeciesId; w: number }[] }

/** Methods a water supports, highest first-fish level first. */
export function methodsFor(water: WaterKind): Method[] {
  const lv = new Map<Method, number>();
  for (const id of SPECIES_IDS) { const s = speciesDef(id); if (s.waters.includes(water)) lv.set(s.method, Math.min(lv.get(s.method) ?? 99, s.level)); }
  return [...lv.keys()].sort((a, b) => lv.get(b)! - lv.get(a)!);
}
export const speciesIn = (water: WaterKind, method: Method) => SPECIES_IDS.filter(id => { const s = speciesDef(id); return s.method === method && s.waters.includes(water); });

/** How likely `id` is to be the fish that bites, against the others caught the same way. 0 = not biting now. */
export function speciesWeight(id: SpeciesId, fishingLevel: number, ctx: FishCtx): number {
  const s = speciesDef(id);
  if (fishingLevel < s.level) return 0;
  const night = isNight(ctx.dayT);
  if (s.time === 'night' && !night) return 0;
  let w = s.freq * Math.min(1, 0.35 + (fishingLevel - s.level) * 0.06);
  if (s.time === 'twilight') w *= isTwilight(ctx.dayT) ? 1.8 : 0.8;
  if (s.time === 'nocturnal' && night) w *= 1.6;
  if (s.rain) { if (ctx.weather === 'clear') return 0; w *= ctx.weather === 'rain' ? 2.5 : 0.7; }
  if (ctx.hidden && s.level >= 14) w *= 1.5;
  return w;
}

const join = (xs: string[]) => (xs.length <= 1 ? xs[0] ?? '' : `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`);

/** "Bait-fish for perch or pike" */
export function methodLabel(water: WaterKind, m: Method, fishingLevel?: number) {
  const fish = speciesIn(water, m).filter(id => fishingLevel === undefined || fishingLevel >= SPECIES[id].level).map(id => SPECIES[id].plural);
  return `${METHODS[m].verb} ${join(fish.length ? fish : speciesIn(water, m).map(id => SPECIES[id].plural))}`;
}

/** What this character would do at a spot of `water` right now, or why they can't. */
export function fishingChoice(c: CharacterState, water: WaterKind, ctx: FishCtx): { choice: FishChoice | null; blocker: Blocker | null; label: string } {
  const L = level(c, 'fishing');
  let soft: Blocker | null = null, softLabel = '';
  const methods = methodsFor(water);
  for (const m of methods) {
    const def = METHODS[m], tool = bestTool(c, def.line);
    if (!tool) continue;
    const fish = speciesIn(water, m), lowest = Math.min(...fish.map(id => SPECIES[id].level));
    if (L < lowest) { soft ??= { text: `You need Fishing ${lowest} to ${def.verb.toLowerCase()} ${SPECIES[fish.find(f => SPECIES[f].level === lowest)!].plural} here.`, short: `${SKILL_NAMES.fishing} ${lowest}` }; continue; }
    if (def.uses) { const b = supplyBlocker(c, def.uses); if (b) { soft ??= b; softLabel ||= methodLabel(water, m, L); continue; } }
    const tier = TOOLS[tool].tier;
    const small = fish.filter(id => tooSmall(tool, id));
    const species = fish.map(id => ({ id, w: speciesWeight(id, L, ctx) })).filter(x => x.w > 0 && (speciesDef(x.id).tier ?? 0) <= tier && !small.includes(x.id));
    if (!species.length && small.length && fish.every(id => small.includes(id) || L < SPECIES[id].level)) { soft ??= { text: `${join(small.map(id => SPECIES[id].plural)).replace(/^./, ch => ch.toUpperCase())} are too small for your ${TOOLS[tool].name.toLowerCase()}. Bring lighter gear for those.`, short: 'gear too heavy' }; softLabel ||= methodLabel(water, m, L); continue; }
    const outclassed = fish.find(id => L >= SPECIES[id].level && (speciesDef(id).tier ?? 0) > tier);
    if (!species.length && outclassed) { soft ??= { text: `${SPECIES[outclassed].name} need better gear than your ${TOOLS[tool].name.toLowerCase()}.`, short: 'better gear' }; softLabel ||= methodLabel(water, m, L); continue; }
    if (!species.length) {
      const nightOnly = fish.find(id => speciesDef(id).time === 'night'), stormy = fish.find(id => speciesDef(id).rain);
      soft ??= nightOnly && !isNight(ctx.dayT) ? { text: `The water is dark and still. ${SPECIES[nightOnly].name} only rise at night${stormy ? `, and ${SPECIES[stormy].plural} only in rough weather` : ''}.`, short: 'night only' }
        : stormy ? { text: `Nothing big is moving. ${SPECIES[stormy].name} only come up when the weather turns.`, short: 'rough weather' }
        : { text: 'Nothing is biting here right now.', short: 'nothing biting' };
      softLabel ||= methodLabel(water, m, L);
      continue;
    }
    return { choice: { method: m, tool, species }, blocker: null, label: `${def.verb} ${join(species.map(x => SPECIES[x.id].plural))}${outclassed ? ` (${SPECIES[outclassed].plural} need better gear)` : ''}` };
  }
  if (soft) return { choice: null, blocker: soft, label: softLabel || `Fish ${WATERS[water].name.toLowerCase()}` };
  // Carrying nothing that works here: point at the first way in.
  const first = [...methods].reverse()[0], fish = speciesIn(water, first), lowest = Math.min(...fish.map(id => SPECIES[id].level));
  const label = methodLabel(water, first);
  if (L < lowest) return { choice: null, blocker: { text: `You need Fishing ${lowest} to fish here.`, short: `${SKILL_NAMES.fishing} ${lowest}` }, label };
  // suggest the best way in that their level allows
  const want = methods.find(m => L >= Math.min(...speciesIn(water, m).map(id => SPECIES[id].level))) ?? first;
  const b = toolBlocker(c, METHODS[want].line, 0, `to ${METHODS[want].verb.toLowerCase()} ${join(speciesIn(water, want).map(id => SPECIES[id].plural))}`);
  return { choice: null, blocker: b ?? { text: 'You have nothing to fish with.', short: 'gear' }, label: methodLabel(water, want) };
}

/**
 * Heavy tackle doesn't take small fish: past the first tier of a line, anything more than 20
 * levels below what the tool needs slips through the mesh or ignores the hook. It's how you
 * fish *for* sturgeon in a lake full of perch.
 */
export const tooSmall = (tool: ToolId, id: SpeciesId) => TOOLS[tool].tier >= 1 && speciesDef(id).level < TOOLS[tool].use[1] - 20;

/** Pick which fish bites, from weights and a 0..1 roll. */
export function pickSpecies(species: { id: SpeciesId; w: number }[], r: number): SpeciesId {
  const total = species.reduce((a, s) => a + s.w, 0);
  let x = r * total;
  for (const s of species) { x -= s.w; if (x <= 0) return s.id; }
  return species[species.length - 1].id;
}

export function fishChance(c: CharacterState, id: SpeciesId, ctx: FishCtx): number {
  const s = speciesDef(id);
  let base = 0.3 + (level(c, 'fishing') - s.level) * 0.025 + toolPower(c, METHODS[s.method].line);
  if (ctx.weather === 'rain') base += 0.08;
  if (ctx.hidden) base += 0.1;
  const fed = c.fedTicks > 0 ? 1.15 : 1;
  return Math.min(0.95, Math.max(0.12, base * fed));
}

/** Weight of one catch. Most fish are small; skill and harpoons nudge it up. */
export function rollKg(id: SpeciesId, fishingLevel: number, r: number, bonus = 1): number {
  const s = speciesDef(id);
  const skill = 1 + Math.min(0.25, Math.max(0, fishingLevel - s.level) * 0.006);
  const kg = s.kg[0] + (s.kg[1] - s.kg[0]) * Math.pow(r, 2.4) * skill * bonus;
  return Math.round(Math.min(kg, s.kg[1] * 1.1) * 100) / 100;
}

export const isHarpoon = (tool: ToolId) => TOOLS[tool].line === 'harpoon';
